import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PrivacySettings = ({ data, onUpdate }) => {
  const [localData, setLocalData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  const handleSave = () => {
    onUpdate(localData);
    setHasChanges(false);
  };

  const updatePrivacySetting = (setting, value) => {
    setLocalData(prev => ({
      ...prev,
      [setting]: value
    }));
    setHasChanges(true);
  };

  const updateDataSharing = (setting, value) => {
    setLocalData(prev => ({
      ...prev,
      dataSharing: {
        ...prev?.dataSharing,
        [setting]: value
      }
    }));
    setHasChanges(true);
  };

  const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
    <label className={`relative inline-flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
    </label>
  );

  const PrivacySection = ({ title, icon, children }) => (
    <div className="bg-card rounded-lg p-6 border">
      <h3 className="text-lg font-medium text-foreground mb-6 flex items-center">
        <Icon name={icon} size={20} className="mr-2" />
        {title}
      </h3>
      <div className="space-y-6">
        {children}
      </div>
    </div>
  );

  const SettingRow = ({ title, description, children }) => (
    <div className="flex items-start justify-between py-2">
      <div className="flex-1 pr-4">
        <label className="text-sm font-medium text-foreground block">
          {title}
        </label>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">
            {description}
          </p>
        )}
      </div>
      <div className="flex items-center">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Privacy Settings</h2>
          <p className="text-muted-foreground mt-1">
            Control your privacy preferences and data sharing
          </p>
        </div>
        
        {hasChanges && (
          <Button
            onClick={handleSave}
            iconName="Save"
            size="sm"
          >
            Save Changes
          </Button>
        )}
      </div>
      {/* Profile Visibility */}
      <PrivacySection title="Profile Visibility" icon="Eye">
        <SettingRow
          title="Profile Visibility"
          description="Control who can see your profile information"
        >
          <select
            value={localData?.profileVisibility || 'private'}
            onChange={(e) => updatePrivacySetting('profileVisibility', e?.target?.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="public">Public</option>
            <option value="friends">Friends Only</option>
            <option value="private">Private</option>
          </select>
        </SettingRow>

        <SettingRow
          title="Show Travel History"
          description="Allow other users to see your completed trips"
        >
          <ToggleSwitch
            checked={localData?.showTravelHistory || false}
            onChange={(e) => updatePrivacySetting('showTravelHistory', e?.target?.checked)}
          />
        </SettingRow>

        <SettingRow
          title="Review Visibility"
          description="Control who can see your reviews and ratings"
        >
          <select
            value={localData?.reviewVisibility || 'public'}
            onChange={(e) => updatePrivacySetting('reviewVisibility', e?.target?.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="public">Public</option>
            <option value="travelers">Travelers Only</option>
            <option value="private">Private</option>
          </select>
        </SettingRow>

        <SettingRow
          title="Contact from Other Travelers"
          description="Allow other travelers to contact you"
        >
          <ToggleSwitch
            checked={localData?.contactFromOtherTravelers || false}
            onChange={(e) => updatePrivacySetting('contactFromOtherTravelers', e?.target?.checked)}
          />
        </SettingRow>
      </PrivacySection>
      {/* Data Sharing */}
      <PrivacySection title="Data Sharing & Analytics" icon="Database">
        <SettingRow
          title="Analytics & Performance"
          description="Help us improve our service by sharing anonymous usage data"
        >
          <ToggleSwitch
            checked={localData?.dataSharing?.analytics || false}
            onChange={(e) => updateDataSharing('analytics', e?.target?.checked)}
          />
        </SettingRow>

        <SettingRow
          title="Marketing Insights"
          description="Share data to receive personalized travel recommendations"
        >
          <ToggleSwitch
            checked={localData?.dataSharing?.marketing || false}
            onChange={(e) => updateDataSharing('marketing', e?.target?.checked)}
          />
        </SettingRow>

        <SettingRow
          title="Third-Party Partners"
          description="Share data with trusted travel partners for better service"
        >
          <ToggleSwitch
            checked={localData?.dataSharing?.thirdPartyPartners || false}
            onChange={(e) => updateDataSharing('thirdPartyPartners', e?.target?.checked)}
          />
        </SettingRow>

        <SettingRow
          title="Social Media Integration"
          description="Allow integration with social media platforms"
        >
          <ToggleSwitch
            checked={localData?.dataSharing?.socialMediaIntegration || false}
            onChange={(e) => updateDataSharing('socialMediaIntegration', e?.target?.checked)}
          />
        </SettingRow>
      </PrivacySection>
      {/* Location & Activity */}
      <PrivacySection title="Location & Activity" icon="MapPin">
        <SettingRow
          title="Location Sharing"
          description="Share your location for better local recommendations"
        >
          <ToggleSwitch
            checked={localData?.locationSharing || false}
            onChange={(e) => updatePrivacySetting('locationSharing', e?.target?.checked)}
          />
        </SettingRow>
      </PrivacySection>
      {/* Communications */}
      <PrivacySection title="Marketing Communications" icon="MessageCircle">
        <SettingRow
          title="Marketing Communications"
          description="Receive marketing emails and promotional content"
        >
          <ToggleSwitch
            checked={localData?.marketingCommunications || false}
            onChange={(e) => updatePrivacySetting('marketingCommunications', e?.target?.checked)}
          />
        </SettingRow>
      </PrivacySection>
      {/* Privacy Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Privacy Information</h4>
            <div className="text-xs text-blue-800 space-y-2">
              <p>
                Your privacy is important to us. We collect and use your data in accordance with our 
                <button className="underline hover:text-blue-900 ml-1">Privacy Policy</button>.
              </p>
              <p>
                You can request a copy of your data, update your preferences, or delete your account at any time.
              </p>
              <div className="flex flex-wrap gap-4 mt-3">
                <button className="text-blue-700 hover:text-blue-900 underline text-xs">
                  View Privacy Policy
                </button>
                <button className="text-blue-700 hover:text-blue-900 underline text-xs">
                  Download My Data
                </button>
                <button className="text-blue-700 hover:text-blue-900 underline text-xs">
                  Data Protection Rights
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;